/**
 * DeploymentTab displays large-scale deployment guidance and agentless mode information.
 *
 * Extracted from agents/index.tsx Phase 1.1 refactor (2025-10-22)
 *
 * Features:
 * - Deployment automation tools (Ansible, GPO, JAMF, SimpleMDM, SCCM)
 * - Agentless mode instructions for network share deployment
 * - Support contact information
 */
export const DeploymentTab = () => {
  return (
    <div className="border-chariot-stroke space-y-6 rounded-lg border p-6">
      {/* Header section */}
      <div className="space-y-2">
        <h2 className="text-chariot-text-primary text-xl font-bold">
          Deploy the Endpoint at Scale
        </h2>
        <p className="text-chariot-text-secondary">
          Before starting, ensure the following:
        </p>
      </div>

      {/* Divider */}
      <div className="border-chariot-stroke border-t"></div>

      {/* Agent Deployment section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <h3 className="text-chariot-text-primary text-lg font-semibold">
            Endpoint Deployment
          </h3>
          <p className="text-chariot-text-secondary">
            Use your preferred tool to automate the install:
          </p>
        </div>

        {/* Tool icons box */}
        <div className="border-chariot-stroke rounded-lg border p-6">
          <div className="flex items-center justify-between space-x-8">
            <div className="flex flex-row items-center gap-2">
              <img src="/icons/ansible.svg" className="size-6" alt="Ansible" />
              <span className="text-chariot-text-primary text-sm font-medium">
                Ansible
              </span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <img src="/icons/gpo.svg" className="size-6" alt="GPO" />
              <span className="text-chariot-text-primary text-sm font-medium">
                GPO
              </span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <img src="/icons/jamf.svg" className="size-6" alt="JAMF" />
              <span className="text-chariot-text-primary text-sm font-medium">
                JAMF
              </span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <img
                src="/icons/simplemdm.svg"
                className="size-6"
                alt="SimpleMDM"
              />
              <span className="text-chariot-text-primary text-sm font-medium">
                SimpleMDM
              </span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <img src="/icons/sccm.svg" className="size-6" alt="SCCM" />
              <span className="text-chariot-text-primary text-sm font-medium">
                SCCM
              </span>
            </div>
          </div>
        </div>

        {/* Support message */}
        <p className="text-chariot-text-secondary mb-4 text-sm">
          Deployment depends on your environment -- coordinate with{' '}
          <a
            href="mailto:support@praetorian.com"
            className="text-chariot-brand-primary font-medium hover:underline"
          >
            Praetorian Support
          </a>{' '}
          for guidance.
        </p>
      </div>

      {/* Another divider */}
      <div className="border-chariot-stroke border-t"></div>

      {/* Agentless Mode section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <h3 className="text-chariot-text-primary text-lg font-semibold">
            Agentless Mode (No Install)
          </h3>
          <p className="text-chariot-text-secondary">
            Run Aegis from a network share -- ideal for environments where
            persistent installs aren&apos;t allowed.
          </p>
        </div>

        {/* Setup Steps */}
        <div className="space-y-3">
          <h4 className="text-md text-chariot-text-primary font-semibold">
            Setup Steps:
          </h4>
          <ul className="text-chariot-text-secondary list-inside list-disc space-y-2">
            <li>
              <strong>Create Share:</strong> Host Aegis binary + config with
              read-only access.
            </li>
            <li>
              <strong>Schedule via GPO:</strong> Use Group Policy to run the
              endpoint on domain machines.
            </li>
            <li>
              <strong>Run & Exit:</strong> Endpoint collects artifacts and exits
              (no install remains).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
